import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TriggerConfiguration } from '@models/adminstration/machine/trigger/trigger-configuration';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { TranslateService } from '@ngx-translate/core';
import { TriggerConfigurationService } from '@services/administration/machine/trigger/trigger-configuration.service';
import { ConfirmationDialogService } from '@services/confirmation-dialog.service';
import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { UtilsService } from '@services/utils/utils.service';

@Component({
    selector: 'scitis-machine-group-trigger-configuration-action',
    templateUrl: './machine-group-trigger-configuration-action.component.html',
    styleUrls: ['./machine-group-trigger-configuration-action.component.scss'],
    standalone: false
})
export class MachineGroupTriggerConfigurationActionComponent implements OnInit, OnChanges {

  @Input()
  public userRole: Role = new Role(new User());

  @Input()
  public triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();

  @Input()
  public isGapDetectionInProgress: object = {};

  @Input()
  public isTriggerCanBeActivate: boolean | undefined = undefined;

  @Output()
  public deleteConfig: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  public editConfig: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public forceTriggerExecution: EventEmitter<TriggerConfiguration> = new EventEmitter<TriggerConfiguration>();

  @Output()
  public activateTrigger: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  public deactivateTrigger: EventEmitter<number> = new EventEmitter<number>();

  public deleteButtonIsDisabled: boolean = true;
  public activateButtonIsDisabled: boolean = true;
  public deactivateButtonIsDisabled: boolean = true;
  public showActivateTriggerButton: boolean = false;
  public showDeactivateTriggerButton: boolean = false;

  constructor(
    private triggerConfigurationService: TriggerConfigurationService,
    private translate: TranslateService,
    private confirmationDialogService: ConfirmationDialogService,
    private loadingIndicator: LoadingIndicatorService,
    private utilsService: UtilsService,
  ) {
  }

  ngOnInit(): void {
    this.checkDisabling();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.checkDisabling();
  }

  public onDeleteTriggerConfiguration(): void {
    const deleteMessageBody = this.translate.instant('Do you really want to permanently delete the trigger configuration %s ?')
      .replace('%s', this.triggerConfiguration.name.toBold());
    const deleteMessageTitle = this.translate.instant('Delete trigger configuration');
    const deleteMessageOK = this.translate.instant('Delete');

    this.confirmationDialogService.handleConfirmationDialog(deleteMessageTitle, deleteMessageBody, '', deleteMessageOK)
      .subscribe(userConfirmed => {
        if (userConfirmed) {
          this.deleteConfig.emit(this.triggerConfiguration.id);
        }
      });
  }

  public onEditTriggerConfiguration(): void {
    this.editConfig.emit();
  }

  public onActivateTriggerConfig(): void {
    const activateMessageTitle = this.translate.instant('Activate trigger');
    const activateMessageOK = this.translate.instant('Activate');
    const activateMessageBody = this.translate.instant('Do you really want to activate trigger %s ?')
      .replace('%s', this.triggerConfiguration.name.toBold());
    this.confirmationDialogService.handleConfirmationDialog(activateMessageTitle,
      activateMessageBody,
      '',
      activateMessageOK
    ).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.loadingIndicator.show(this.translate.instant('Activating'));
        this.triggerConfiguration.isActive = true;
        this.checkDisabling();
        this.activateTrigger.emit(this.triggerConfiguration.id);
      }
    });
  }

  public onDeactivateTriggerConfig(): void {
    const deactivateMessageTitle = this.translate.instant('Deactivate trigger');
    const deactivateMessageOK = this.translate.instant('Deactivate');
    let deactivateMessageBody = this.translate.instant('Do you really want to deactivate trigger %s?')
      .replace('%s', this.triggerConfiguration.name.toBold());
    if (this.isGapDetectionInProgress[this.triggerConfiguration.id]) {
      deactivateMessageBody = this.translate.instant('Gap detection in progress. Gap detection process will be interrupted for current trigger.') +
        '</br>' + deactivateMessageBody;
    }

    this.confirmationDialogService.handleConfirmationDialog(
      deactivateMessageTitle,
      deactivateMessageBody,
      '',
      deactivateMessageOK
    ).subscribe(userConfirmed => {
      if (userConfirmed) {
        this.loadingIndicator.show(this.translate.instant('Deactivating'));
        this.triggerConfiguration.isActive = false;
        this.checkDisabling();
        this.deactivateTrigger.emit(this.triggerConfiguration.id);
      }
    });
  }

  private setShowDeactivateTriggerButton(): void {
    this.showDeactivateTriggerButton = this.triggerConfigurationService.showDeactivateTriggerButton(this.triggerConfiguration, this.isGapDetectionInProgress);
  }

  private setShowActivateTriggerButton(): void {
    this.showActivateTriggerButton = this.triggerConfigurationService.showActivateTriggerButton(this.triggerConfiguration, this.isGapDetectionInProgress);
  }

  private setActivateButtonIsDisabled(): void {
    this.activateButtonIsDisabled = this.triggerConfigurationService.isActivateTriggerConfigurationButtonDisabled(this.userRole.isAdminRole,
      this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole, this.isTriggerCanBeActivate);
  }

  private setDeactivateButtonIsDisabled(): void {
    this.deactivateButtonIsDisabled = !this.utilsService.isAdminOrHigher(this.userRole.isAdminRole, this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole);
  }

  private setDeleteButtonIsDisabled(): void {
    this.deleteButtonIsDisabled = this.triggerConfigurationService.isDeleteTriggerConfigurationButtonDisabled(this.userRole.isAdminRole,
      this.userRole.isTechAdminRole, this.userRole.isSuperAdminRole, this.triggerConfiguration, this.isGapDetectionInProgress);
  }

  private checkDisabling(): void {
    this.setDeleteButtonIsDisabled();
    this.setActivateButtonIsDisabled();
    this.setDeactivateButtonIsDisabled();
    this.setShowDeactivateTriggerButton();
    this.setShowActivateTriggerButton();
  }
}
